import type { LearningCourse } from '@/core/domain/learning/course/LearningCourse'
import type { ReferentialAxis } from '@/core/domain/referential/axis/ReferentialAxis'

/**
 * Interface pour les couleurs des tags
 */
export interface AxisColorSet {
  tag: string
}

/**
 * Couleurs définies pour chaque axe du référentiel
 * Index 0: Axe 1, Index 1: Axe 2, Index 2: Axe 3
 */
export const AXIS_COLORS: AxisColorSet[] = [
  { tag: 'bg-blue-300 text-blue-900' }, // Axe 1 - Bleu
  { tag: 'bg-green-300 text-green-900' }, // Axe 2 - Vert
  { tag: 'bg-orange-300 text-orange-900' } // Axe 3 - Orange
]

/**
 * Couleur par défaut si aucun axe n'est trouvé
 */
export const DEFAULT_AXIS_COLOR: AxisColorSet = {
  tag: 'bg-gray-300 text-gray-900'
}

/**
 * Récupère la couleur d'un cours basée sur son axe du référentiel
 * @param course Le cours d'apprentissage
 * @param axis L'axe du référentiel (peut être null)
 * @returns Un objet contenant les classes CSS pour les tags
 */
export function getCourseColorByAxis(
  course: LearningCourse,
  axis: ReferentialAxis | null
): AxisColorSet {
  if (!axis) {
    return DEFAULT_AXIS_COLOR
  }

  // Utiliser l'ordre de l'axe pour déterminer la couleur (en supposant que order commence à 1)
  const axisIndex = (axis.order - 1) % AXIS_COLORS.length
  return AXIS_COLORS[axisIndex] || AXIS_COLORS[0]
}

/**
 * Récupère la couleur d'un axe par son ordre
 * @param axisOrder L'ordre de l'axe (commence à 1)
 * @returns Un objet contenant les classes CSS pour les tags
 */
export function getAxisColorByOrder(axisOrder: number): AxisColorSet {
  const axisIndex = (axisOrder - 1) % AXIS_COLORS.length
  return AXIS_COLORS[axisIndex] || AXIS_COLORS[0]
}

/**
 * Récupère toutes les couleurs disponibles pour les axes
 * @returns Un tableau de toutes les couleurs d'axes disponibles
 */
export function getAllAxisColors(): AxisColorSet[] {
  return [...AXIS_COLORS]
}
